import { FormControl, InputLabel } from "@material-ui/core";
import genericSelectInputStyle from "assets/jss/generic/genericSelectInputStyle.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import { Proxy } from "core";
import withArtifex from "core/withArtifex";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import AsyncSelect from "react-select/lib/Async";
import GenericVirtualizedMenuList from "./GenericSelectInput/GenericVirtualizedMenuList";

const customSelectComponents = { MenuList: GenericVirtualizedMenuList };

class GenericAsyncAutocomplete extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			selectedOption: null,
		};

		this.handleChange = this.handleChange.bind(this);
		this.loadOptions = this.loadOptions.bind(this);
		this.timeoutId = null;
		this.placeHolderText = "Type to search...";
		this.selectProps = {};
	}

	getOptionValue = (item) => {
		const { KeyValueMember = "Id", ResolveKeyValue } = this.props;
		if (typeof ResolveKeyValue === "function") {
			return ResolveKeyValue(item);
		}
		return item?.[KeyValueMember];
	}

	getOptionLabel = (item) => {
		const { TextValueMember = "ParameterDesc", ResolveTextValue } = this.props;
		if (typeof ResolveTextValue === "function") {
			return ResolveTextValue(item);
		}
		return item?.[TextValueMember];
	}

	componentDidMount() {
		const { Value, DefaultText, Url } = this.props;
		if (Value != null && DefaultText) {
			this.setState({
				selectedOption: { value: Value, label: DefaultText }
			});
		} else if (Value != null && !DefaultText && Url) {
			// Auto-resolve label when Value exists but DefaultText is missing
			this.loadSingleOption(Value);
		}
	}

	loadSingleOption(value) {
		const { Url, GetByIdUrl, DataRoot = "Item", SearchParam = "searchTerm" } = this.props;
		const urls = [
			GetByIdUrl ? this.createUrlWithQuery(GetByIdUrl, "id", value) : null,
			this.createUrlWithQuery(Url, SearchParam, value),
			this.createUrlWithQuery(Url, "id", value),
			this.createUrlWithQuery(Url, "Id", value),
			Url
		].filter((url, index, list) => url && list.indexOf(url) === index);

		this.loadSingleOptionFromUrls(value, urls, DataRoot, 0);
	}

	createUrlWithQuery(url, queryName, queryValue) {
		return url.includes("?") ? `${url}&${queryName}=${queryValue}` : `${url}?${queryName}=${queryValue}`;
	}

	loadSingleOptionFromUrls(value, urls, dataRoot, index) {
		if (index >= urls.length) {
			return;
		}

		const targetUrl = urls[index];

		Proxy.POST(targetUrl, {}, (response) => {
			let data = response[dataRoot] || [];
			if (!Array.isArray(data)) {
				data = [data];
			}

			const matched = data.find(d => this.getOptionValue(d) == value);

			if (matched) {
				this.setState({
					selectedOption: {
						value: this.getOptionValue(matched),
						label: this.getOptionLabel(matched),
						data: matched
					}
				});
				return;
			}

			this.loadSingleOptionFromUrls(value, urls, dataRoot, index + 1);
		}, () => {
			this.loadSingleOptionFromUrls(value, urls, dataRoot, index + 1);
		});
	}

	componentDidUpdate(prevProps) {
		const { Value, DefaultText, Url } = this.props;
		if (Value !== prevProps.Value || DefaultText !== prevProps.DefaultText) {
			if (Value == null || Value === "") {
				this.setState({ selectedOption: null });
			}
			else if (DefaultText) {
				this.setState({ selectedOption: { value: Value, label: DefaultText } });
			}
			else if (Url) {
				this.loadSingleOption(Value);
			}
		}
	}

	handleChange(selectedOption) {
		const { Name, ValueChanged } = this.props;
		this.setState({ selectedOption });
		if (ValueChanged) {
			const newValue = selectedOption ? selectedOption.value : null;
			ValueChanged(Name, newValue, selectedOption ? selectedOption.data : null);
		}
	}

	loadOptions(inputValue, callback) {
		const { Url, MinLength = 3, DebounceMs = 500, SearchParam = "searchTerm", DataRoot = "Item", FilterPredicate } = this.props;

		if (!inputValue || inputValue.length < MinLength) {
			callback([]);
			return;
		}

		if (this.timeoutId) {
			clearTimeout(this.timeoutId);
		}

		this.timeoutId = setTimeout(() => {
			const targetUrl = Url.includes("?") ? `${Url}&${SearchParam}=${inputValue}` : `${Url}?${SearchParam}=${inputValue}`;
			const onSuccess = (response) => {
				let data = response[DataRoot] || [];
				if (FilterPredicate && typeof FilterPredicate === "function") {
					data = data.filter(FilterPredicate);
				}

				const options = data
					.map(d => ({
						value: this.getOptionValue(d),
						label: this.getOptionLabel(d),
						data: d
					}))
					.filter(x => x.value !== null && x.value !== undefined && x.value !== "");

				callback(options);
			};

			const onError = (error) => {
				console.error("AsyncAutocomplete Error", error);
				callback([]);
			};

			Proxy.POST(targetUrl, {}, onSuccess, onError);
		}, DebounceMs);
	}

	GetStyles() {
		const { Required, IsInvalid } = this.props;

		let borderColor = "#e9eaec";
		if (IsInvalid) {
			// eslint-disable-next-line no-undef
			borderColor = globalThis.InvalidFieldColor || "#f44336";
		}
		else if (Required) {
			// eslint-disable-next-line no-undef
			borderColor = globalThis.RequiredFieldColor || "#ff9800";
		}

		return {
			control: (base) => (
				{
					...base,
					height: "25px",
					minHeight: "25px",
					fontSize: "12px",
					border: `1px solid ${borderColor}`,
					borderRadius: "unset",
					"&:active": {
						border: `1px solid ${borderColor} !important`,
						boxShadow: "unset !important"
					},
					fontWeight: "400"
				}),
			valueContainer: (base) => (
				{
					...base,
					height: "25px",
					minHeight: "25px",
					fontSize: "12px",
					fontWeight: "400",
					"&:active": {
						background: "unset",
						border: `1px solid ${borderColor} !important`,
						boxShadow: "unset !important",
						padding: "unset"
					},
					padding: "unset"
				}),
			indicatorsContainer: (base) => (
				{
					...base,
					height: "25px",
					minHeight: "25px",
					fontSize: "12px",
					fontWeight: "400",
					"&:active": {
						border: `1px solid ${borderColor} !important`,
						boxShadow: "unset !important"
					}
				}),
			option: (base) => (
				{
					...base,
					fontSize: "12px",
					fontWeight: "400",
					"&:active": {
						border: `1px solid ${borderColor} !important`,
						boxShadow: "unset !important"
					}
				}),
			container: (base) => ({
				...base
			}),
			menu: (base) => ({
				...base,
				zIndex: "50"
			}),
			singleValue: (base, state) => {
				return {
					...base,
					color: "rgba(0, 0, 0, 1)"
				};
			}
		};
	}

	render() {
		const { classes, LabelText, Name, Disabled, LabelMd, TitleLabel, LabelStyle, SelectStyle, IsTextBold, CanClear, Hidden, VirtualizedList } = this.props;
		const { selectedOption } = this.state;

		if (Hidden) return null;

		const titleLabel = TitleLabel || this.placeHolderText;

		let labelMd = LabelMd == -1 ? undefined : LabelMd;
		let selectMd = LabelMd == -1 ? undefined : 12 - LabelMd;

		if (labelMd == 0) labelMd = undefined;
		if (selectMd == 0) selectMd = undefined;

		this.selectProps.components = (VirtualizedList !== false && VirtualizedList !== undefined) ? customSelectComponents : undefined;

		return (
			<FormControl fullWidth className={classes.selectFormControl}>
				<GridContainer
					container
					direction="row"
					justify="flex-start"
					style={{ marginTop: 6, alignItems: "flex-start" }}>

					{LabelText != undefined && LabelText != "" &&
						<GridItem xs={labelMd} style={LabelStyle}>
							<InputLabel htmlFor={Name} className={IsTextBold ? classes.selectLabelBold : classes.selectLabel} >
								{LabelText}
							</InputLabel>
						</GridItem>
					}

					<GridItem xs={selectMd} style={SelectStyle}>
						<div>
							<AsyncSelect
								cacheOptions={false}
								defaultOptions={false}
								loadOptions={this.loadOptions}
								placeholder={titleLabel}
								isDisabled={Disabled}
								styles={this.GetStyles()}
								value={selectedOption}
								onChange={this.handleChange}
								isClearable={CanClear}
								theme={(theme) => ({
									...theme,
									borderRadius: 0,
									colors: {
										...theme.colors,
										primary: "black",
										primary25: "rgb(246, 246, 246)",
										primary50: "rgb(246, 246, 246)",
										primary75: "rgb(246, 246, 246)"
									},
								})}
								noOptionsMessage={() => "No results found"}
								{...this.selectProps}
							/>
						</div>
					</GridItem>
				</GridContainer>
			</FormControl>
		);
	}
}

GenericAsyncAutocomplete.propTypes = {
	classes: PropTypes.object.isRequired,
	ValueChanged: PropTypes.any,
	Value: PropTypes.any,
	DefaultText: PropTypes.string,
	Name: PropTypes.string,
	LabelText: PropTypes.string,
	KeyValueMember: PropTypes.string,
	TextValueMember: PropTypes.string,
	Url: PropTypes.string.isRequired,
	GetByIdUrl: PropTypes.string,
	SearchParam: PropTypes.string,
	MinLength: PropTypes.number,
	DebounceMs: PropTypes.number,
	DataRoot: PropTypes.string,
	Disabled: PropTypes.bool,
	LabelMd: PropTypes.number,
	TitleLabel: PropTypes.string,
	LabelStyle: PropTypes.object,
	SelectStyle: PropTypes.object,
	IsTextBold: PropTypes.bool,
	CanClear: PropTypes.bool,
	FilterPredicate: PropTypes.func,
	ResolveKeyValue: PropTypes.func,
	ResolveTextValue: PropTypes.func,
	Required: PropTypes.bool,
	IsInvalid: PropTypes.bool,
	VirtualizedList: PropTypes.bool,
	Hidden: PropTypes.bool
};

GenericAsyncAutocomplete.defaultProps = {
	LabelMd: 4,
	CanClear: true,
	MinLength: 3,
	DebounceMs: 500,
	SearchParam: "searchTerm",
	DataRoot: "Item",
	KeyValueMember: "Id",
	TextValueMember: "ParameterDesc"
};

export default withArtifex(GenericAsyncAutocomplete, genericSelectInputStyle);
